import { useEffect, useState } from 'react';
import {
  Edit,
  SelectInput,
  SimpleForm,
  TextInput,
  useNotify,
  useRecordContext,
  useRefresh,
  email,
  required,
} from 'react-admin';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';

import { apiFetch } from '../../api/client';

interface RoleOption {
  id: string;
  name: string;
}

function ResetPasswordPanel() {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (!record?.id) return null;

  const handleReset = async () => {
    if (password.length < 8) {
      notify('Password must be at least 8 characters', { type: 'warning' });
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`/admin/users/${record.id}/reset-password`, {
        method: 'POST',
        body: { password },
      });
      notify('Password reset successfully', { type: 'success' });
      setPassword('');
      refresh();
    } catch (err) {
      notify((err as Error).message || 'Could not reset password', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Reset password
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          type="password"
          label="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          inputProps={{ minLength: 8 }}
          fullWidth
        />
        <Button variant="outlined" disabled={busy || !password} onClick={handleReset}>
          Reset
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        To change your own password, use the profile menu → Change password.
      </Typography>
    </Paper>
  );
}

function UserEditForm() {
  const record = useRecordContext();
  const [roles, setRoles] = useState<RoleOption[]>([]);

  useEffect(() => {
    apiFetch<{ data: Array<{ id: number; name: string }> }>('/admin/roles')
      .then((res) =>
        setRoles(
          res.data
            .filter((r) => r.name !== 'business_owner')
            .map((r) => ({ id: r.name, name: r.name.replace(/_/g, ' ') })),
        ),
      )
      .catch(() => setRoles([]));
  }, []);

  const isOwner = record?.role === 'business_owner';

  return (
    <SimpleForm>
      <TextInput source="full_name" label="Full name" validate={[required()]} fullWidth />
      <TextInput source="email" validate={[required(), email()]} fullWidth />
      {!isOwner && (
        <SelectInput source="role" label="Role" choices={roles} validate={[required()]} fullWidth />
      )}
      <SelectInput
        source="status"
        choices={[
          { id: 'active', name: 'Active' },
          { id: 'inactive', name: 'Inactive' },
          { id: 'suspended', name: 'Suspended' },
        ]}
        validate={[required()]}
        fullWidth
      />
      <Box sx={{ width: '100%' }}>
        <ResetPasswordPanel />
      </Box>
    </SimpleForm>
  );
}

export function UserEdit() {
  return (
    <Edit>
      <UserEditForm />
    </Edit>
  );
}
